/**
 * Shared price utilities — single source of truth for parsing & sorting prices
 * across Home, Shop, Search, and any future listing surface.
 *
 * Why this exists:
 *  - Backend sometimes returns price as a number, sometimes as a string,
 *    sometimes with currency labels ("250,000 SDG", "٢٥٠٠٠ جنيه").
 *  - Naive `Number(price)` returns NaN for any of those, which earlier
 *    caused price-sort to silently treat them as 0 → cheap-looking ads
 *    appeared after expensive ones on Home/Shop.
 *  - This module normalizes all formats to a finite number; missing or
 *    unparseable prices use a sentinel that always sorts to the END.
 */

// Arabic-Indic digits → Latin digits map.  Covers Eastern Arabic (٠١٢٣٤٥٦٧٨٩)
// and Persian (۰۱۲۳۴۵۶۷۸۹) — both seen in Sudanese listings.
const AR_DIGIT_MAP = {
  "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4",
  "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9",
  "۰": "0", "۱": "1", "۲": "2", "۳": "3", "۴": "4",
  "۵": "5", "۶": "6", "۷": "7", "۸": "8", "۹": "9",
};

/**
 * Parse a price value of any shape into a finite Number, or `null` if
 * the value can't be interpreted as a real price.  Never throws.
 */
export function parsePrice(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") {
    return Number.isFinite(raw) && raw >= 0 ? raw : null;
  }
  if (typeof raw !== "string") {
    // booleans, objects, arrays — treat as missing
    return null;
  }

  // Convert Arabic-Indic digits, strip non-numeric chars except dot.
  // Keep dot for decimals.  Drop comma (thousand separator), spaces,
  // currency labels (SDG, جنيه, $, €, etc.), letters.
  let s = "";
  for (const ch of raw) {
    s += AR_DIGIT_MAP[ch] || ch;
  }
  // Replace Arabic decimal separator (٫) with dot
  s = s.replace(/٫/g, ".");
  // Strip everything except digits and the (first) decimal point
  // First, remove thousand separators (commas, spaces, NBSP)
  s = s.replace(/[,\s\u00a0_]/g, "");
  // Drop currency / letter / symbol characters
  s = s.replace(/[^0-9.]/g, "");
  // If multiple dots, keep only the first as decimal
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  if (!s || s === ".") return null;
  const n = parseFloat(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Comparator factory — returns a comparator function suitable for
 * Array.prototype.sort that orders ads by price.  Items with missing /
 * unparseable prices are always pushed to the END regardless of direction.
 *
 * @param {"asc"|"desc"} direction
 * @param {(item)=>any} pick - extracts the raw price from an item
 *                             (default: item.price)
 */
export function priceComparator(direction = "asc", pick) {
  const getter = pick || ((x) => (x && x.price !== undefined ? x.price : null));
  const isAsc = direction === "asc";
  return (a, b) => {
    const pa = parsePrice(getter(a));
    const pb = parsePrice(getter(b));
    // Both missing → preserve order (stable)
    if (pa === null && pb === null) return 0;
    // One missing → it goes last (regardless of direction)
    if (pa === null) return 1;
    if (pb === null) return -1;
    return isAsc ? pa - pb : pb - pa;
  };
}

/**
 * Sort an array of ads by price.  Returns a NEW array.  Stable.
 * Use this AFTER all filters/search results are finalized — never before.
 */
export function sortAdsByPrice(items, direction = "asc", pick) {
  if (!Array.isArray(items) || items.length <= 1) return items || [];
  // Array.prototype.sort is stable in modern JS engines (Node 12+, all
  // browsers we target) so equal-price items keep their relative order.
  return [...items].sort(priceComparator(direction, pick));
}

/**
 * Normalized sort-by detection used by Home and Shop.
 * Accepts the various legacy values used throughout the codebase.
 */
export function isPriceAsc(sortBy) {
  return sortBy === "price_low" || sortBy === "price_asc" || sortBy === "cheapest";
}
export function isPriceDesc(sortBy) {
  return sortBy === "price_high" || sortBy === "price_desc" || sortBy === "expensive";
}
